import { useEffect, useRef } from "react";

// Updated timeline - only from 2020 onwards (newly founded company)
const timeline = [
  { year: "2020", event: "NexEdge Infra founded with a vision to transform Dehradun's real estate landscape with quality plotting and commercial projects." },
  { year: "2021", event: "Launched first residential plotting project 'Nex Greens Enclave' - 12 acres with 120+ premium plots." },
  { year: "2022", event: "Expanded portfolio with commercial project 'Business Hub Dehradun'; team grew to 25+ professionals." },
  { year: "2023", event: "Successfully delivered 3 plotting projects and 2 commercial complexes across Dehradun & Rishikesh." },
  { year: "2024", event: "Completed 15+ projects milestone; recognized as emerging trusted name for property investment in Doon Valley." },
  { year: "2025", event: "Planning expansion to new locations; introducing luxury plotted developments with modern amenities." },
];

// Updated team with realistic names for a newly founded company
const team = [
  { name: "Rahul Negi", role: "Founder & Director", initials: "RN" },
  { name: "Priya Rawat", role: "Head of Sales", initials: "PR" },
  { name: "Anil Kumar", role: "Project Manager", initials: "AK" },
  { name: "Neha Bisht", role: "Legal & Compliance", initials: "NB" },
];

export default function About() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    if (ref.current) ref.current.querySelectorAll(".animate-on-scroll").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-28 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* About intro */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-28">
          <div className="animate-on-scroll">
            <div className="inline-flex items-center gap-2 text-red-600 text-xs font-semibold tracking-widest uppercase
              bg-red-50 border border-red-100 px-4 py-2 rounded-full mb-6">
              Who We Are
            </div>
            <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 'clamp(2.2rem,5vw,3.5rem)' }}
              className="text-red-950 mb-6">
              BUILDING DEHRADUN'S<br/><span className="text-blue-700">FUTURE</span><br/>SINCE 2020
            </h2>
            <p className="text-slate-500 leading-relaxed mb-5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              NexEdge Infra is a trusted real estate development company headquartered in Dehradun, specializing in quality plotting projects and commercial spaces across the Doon Valley.
            </p>
            <p className="text-slate-500 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Our team of dedicated professionals brings deep local expertise, transparent dealings, and an unwavering commitment to quality — ensuring every plot and commercial space we deliver meets the highest standards of customer satisfaction.
            </p>

            <div className="grid grid-cols-2 gap-4 mt-8">
              {[
                { label: "Projects Completed", val: "15+" },
                { label: "Happy Families", val: "400+" },
                { label: "Team Members", val: "25+" },
                { label: "Satisfaction Rate", val: "100%" },
              ].map(item => (
                <div key={item.label} className="bg-red-50 rounded-xl p-4">
                  <div className="text-blue-700 font-bold text-2xl"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {item.val}
                  </div>
                  <div className="text-slate-500 text-xs mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Mission / Vision cards - Updated colors to red/blue */}
          <div className="animate-on-scroll space-y-5" style={{ transitionDelay: '150ms' }}>
            <div className="bg-gradient-to-r from-red-900 to-blue-900 rounded-2xl p-8 text-white">
              <div className="text-red-200 text-xs font-bold tracking-widest uppercase mb-3">Our Mission</div>
              <p className="text-lg leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                To provide genuine, transparent, and quality real estate solutions that help people find their perfect land or commercial space at fair prices.
              </p>
            </div>
            <div className="bg-red-950 rounded-2xl p-8 text-white">
              <div className="text-red-300 text-xs font-bold tracking-widest uppercase mb-3">Our Vision</div>
              <p className="text-lg leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                To become Dehradun's most trusted name in plotted development and commercial real estate — known for honesty, quality, and customer-first approach.
              </p>
            </div>
            <div className="border-2 border-red-100 rounded-2xl p-8">
              <div className="text-red-600 text-xs font-bold tracking-widest uppercase mb-3">Our Values</div>
              <div className="flex flex-wrap gap-2">
                {["Transparency","Quality","Trust","Customer First","Integrity","Timely Delivery"].map(v => (
                  <span key={v} className="bg-red-50 text-red-700 text-sm font-medium px-3 py-1.5 rounded-full border border-red-100">
                    {v}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Timeline - Starting from 2020 */}
        <div className="mb-24">
          <h3 className="text-center text-red-950 mb-12 animate-on-scroll"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '2.2rem' }}>
            OUR <span className="text-blue-700">JOURNEY</span>
          </h3>
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-px top-0 bottom-0 w-0.5 bg-red-100 hidden md:block"/>
            <div className="space-y-8">
              {timeline.map((t, i) => (
                <div key={t.year}
                  className={`animate-on-scroll flex flex-col md:flex-row gap-6 items-start md:items-center
                  ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  style={{ transitionDelay: `${i * 100}ms` }}>
                  <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white border border-red-100 rounded-xl p-5 shadow-sm hover:shadow-md
                      hover:border-red-200 transition-all duration-300 inline-block text-left">
                      <p className="text-slate-600 text-sm leading-relaxed"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}>{t.event}</p>
                    </div>
                  </div>
                  <div className="w-16 h-16 bg-gradient-to-br from-red-900 to-blue-900 rounded-full flex items-center justify-center flex-shrink-0
                    shadow-lg shadow-red-200 z-10">
                    <span className="text-white font-bold text-sm"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{t.year}</span>
                  </div>
                  <div className="flex-1 hidden md:block"/>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Team - Updated with local names */}
        <div>
          <h3 className="text-center text-red-950 mb-12 animate-on-scroll"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '2.2rem' }}>
            OUR <span className="text-blue-700">TEAM</span>
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((m, i) => (
              <div key={m.name}
                className="animate-on-scroll text-center group"
                style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="w-24 h-24 bg-gradient-to-br from-red-900 to-blue-900 rounded-2xl
                  flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-200
                  group-hover:shadow-xl group-hover:scale-105 transition-all duration-300">
                  <span className="text-white font-bold text-2xl"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{m.initials}</span>
                </div>
                <h4 className="text-red-950 font-bold"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.15rem', letterSpacing: '0.02em' }}>
                  {m.name}
                </h4>
                <p className="text-slate-500 text-sm mt-1">{m.role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}